/**
 * Ops ferries (Megan / Dispatcher) — not Inbox RFQs.
 *
 * Emerson gives time + origin + dest. Desk math picks tail/crew when the
 * board makes them obvious, assigns a CEL number, and respects quiet hours.
 * Prefer reinstate when a matching cancelled ferry is still on file.
 *
 * Last-minute pax: reclassify ferry → light revenue charter at a sim
 * discount vs the normal card (CAD home currency). Same ticket / flight number.
 */
import { hydrateSceneryAirport } from "./airports";
import { formatUtcDateTime } from "./clock";
import { curfewClashMessage, ferryTimesLegal, icaoKey, type AirportHours } from "./curfew";
import { spokenTripLabel } from "./flightNumber";
import { isEmptyFerry, isRevenueCharter, type RevenueClassifiable } from "./ledger";
import type {
  AircraftSpec,
  CrewMember,
  FlightType,
  Mission,
  SceneryAirport,
} from "./types";

/** Last-minute ferry fill vs a normal charter card. Company loses less than flying empty. */
export const FERRY_REVENUE_QUOTE_FACTOR = 0.55;

/** Light charter type after a ferry picks up paying souls. */
export const FERRY_REVENUE_FLIGHT_TYPE: FlightType = "corporate";

export type OpsFerryMode = "create" | "reinstate";

export type CancelledFerryHint = {
  originIcao: string;
  destIcao: string;
  departureIso?: string;
  aircraftId?: string;
  registration?: string;
};

export type OpsFerryTailHint = {
  aircraftId?: string;
  registration?: string;
};

export type PlannedOpsFerry =
  | {
      ok: true;
      mode: "reinstate";
      mission: Mission;
      summary: string;
      curfewWarning?: string;
    }
  | {
      ok: true;
      mode: "create";
      originIcao: string;
      destIcao: string;
      departureIso: string;
      spec: AircraftSpec;
      crewId?: string;
      crewName?: string;
      eteMin: number;
      curfewWarning?: string;
      dutyWarning?: string;
      summary: string;
    }
  | { ok: false; error: string };

function sameIcao(a: string | null | undefined, b: string | null | undefined): boolean {
  return icaoKey(a) === icaoKey(b) && Boolean(icaoKey(a));
}

function normalizeReg(raw: string | null | undefined): string {
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s._-]+/g, "");
}

export function parseOpsIcao(raw: string | null | undefined): string {
  return icaoKey(raw);
}

export function parseOpsDeparture(raw: string | null | undefined): Date | null {
  const d = new Date(String(raw ?? "").trim());
  return Number.isFinite(d.getTime()) ? d : null;
}

export function isOpenOpsFerry(m: Pick<Mission, "flightType" | "status" | "isMaintenance">): boolean {
  return m.flightType === "ferry" && (m.status === "pending" || m.status === "in-progress");
}

export function spokenFlightKind(row: RevenueClassifiable): string {
  if (row.flightType === "ferry" && isEmptyFerry(row)) return "ferry";
  if (isRevenueCharter(row)) return "revenue charter";
  return row.flightType === "ferry" ? "ferry" : "charter";
}

/** Most recent cancelled company ferry on this city pair (optional tail / time). */
export function findCancelledFerryMatch(
  missions: readonly Mission[],
  hint: CancelledFerryHint,
): Mission | undefined {
  const origin = parseOpsIcao(hint.originIcao);
  const dest = parseOpsIcao(hint.destIcao);
  if (!origin || !dest) return undefined;
  const wantTail = normalizeReg(hint.registration);
  const wantAc = String(hint.aircraftId ?? "").trim();
  const wantDep = hint.departureIso ? Date.parse(hint.departureIso) : Number.NaN;
  const hits = missions.filter((m) => {
    if (m.status !== "cancelled" || m.flightType !== "ferry") return false;
    if (!sameIcao(m.originIcao, origin) || !sameIcao(m.destIcao, dest)) return false;
    if (wantAc && m.aircraftId !== wantAc) return false;
    if (wantTail && normalizeReg(m.registration) !== wantTail) return false;
    return true;
  });
  if (!hits.length) return undefined;
  if (Number.isFinite(wantDep)) {
    hits.sort((a, b) => Math.abs(Date.parse(a.departureIso) - wantDep) - Math.abs(Date.parse(b.departureIso) - wantDep));
    return hits[0];
  }
  hits.sort((a, b) => Date.parse(b.cancelledAt ?? b.createdAt) - Date.parse(a.cancelledAt ?? a.createdAt));
  return hits[0];
}

export function pickOpsFerryTail(
  fleet: readonly AircraftSpec[],
  missions: readonly Mission[],
  hint: OpsFerryTailHint,
  originIcao: string,
): { ok: true; spec: AircraftSpec } | { ok: false; error: string } {
  const wantId = String(hint.aircraftId ?? "").trim();
  const wantReg = normalizeReg(hint.registration);
  if (wantId || wantReg) {
    const spec = fleet.find((a) => a.id === wantId || normalizeReg(a.registration) === wantReg);
    if (!spec) return { ok: false, error: "That tail is not on the fleet." };
    return { ok: true, spec };
  }
  const origin = parseOpsIcao(originIcao);
  const available = fleet.filter((a) => a.available !== false && a.status !== "grounded" && a.status !== "in_mx");
  const atOrigin = available.filter((a) => sameIcao(a.lastAirportIcao, origin));
  if (atOrigin.length === 1) return { ok: true, spec: atOrigin[0]! };
  const parkedOpen = available.filter((a) => {
    const last = missions.find(
      (m) => m.aircraftId === a.id && (m.status === "pending" || m.status === "in-progress" || m.status === "completed"),
    );
    return last ? sameIcao(last.destIcao, origin) : false;
  });
  if (parkedOpen.length === 1) return { ok: true, spec: parkedOpen[0]! };
  if (available.length === 1) return { ok: true, spec: available[0]! };
  if (atOrigin.length > 1) {
    return {
      ok: false,
      error: `Several tails are at ${origin}: ${atOrigin.map((a) => a.registration).join(", ")}. Name one.`,
    };
  }
  if (available.length > 1) {
    return {
      ok: false,
      error: `Which tail? On the line: ${available.map((a) => a.registration).join(", ")}.`,
    };
  }
  return { ok: false, error: "No available tail on the fleet for an ops ferry." };
}

export function pickOpsFerryCrew(
  crew: readonly CrewMember[],
  missions: readonly Mission[],
  spec: AircraftSpec,
  requestedId?: string,
): { crewId?: string; crewName?: string } {
  if (requestedId) {
    const hit = crew.find((c) => c.id === requestedId);
    if (hit) return { crewId: hit.id, crewName: hit.displayName };
  }
  const onTail = missions.find(
    (m) =>
      m.aircraftId === spec.id &&
      (m.status === "pending" || m.status === "in-progress") &&
      Boolean((m as { assignedCrewId?: string }).assignedCrewId),
  );
  if (onTail) {
    const id = String((onTail as { assignedCrewId?: string }).assignedCrewId ?? "");
    const hit = crew.find((c) => c.id === id);
    if (hit) return { crewId: hit.id, crewName: hit.displayName };
  }
  const pilots = crew.filter((c) => c.roles.includes("pilot"));
  if (pilots.length === 1) return { crewId: pilots[0]!.id, crewName: pilots[0]!.displayName };
  return {};
}

export function opsFerryCurfewWarning(
  fromIcao: string,
  toIcao: string,
  departure: Date,
  eteMin: number,
  hoursFor?: (icao: string | null | undefined) => AirportHours,
): string | undefined {
  const check = ferryTimesLegal(fromIcao, toIcao, departure, eteMin, hoursFor);
  if (check.legal) return undefined;
  return (
    curfewClashMessage(check, hoursFor) ||
    `CURFEW  ferry ${icaoKey(fromIcao)}–${icaoKey(toIcao)} overlaps quiet hours.`
  );
}

export function planOpsFerry(input: {
  originIcao: string;
  destIcao: string;
  departureIso: string;
  missions: readonly Mission[];
  fleet: readonly AircraftSpec[];
  scenery: readonly SceneryAirport[];
  crew?: readonly CrewMember[];
  aircraftId?: string;
  registration?: string;
  crewId?: string;
  preferReinstate?: boolean;
  forceCreate?: boolean;
  eteMinHint?: number;
  hoursFor?: (icao: string | null | undefined) => AirportHours;
}): PlannedOpsFerry {
  const origin = parseOpsIcao(input.originIcao);
  const dest = parseOpsIcao(input.destIcao);
  if (!origin || !dest) return { ok: false, error: "Need a real origin and destination ICAO." };
  if (origin === dest) return { ok: false, error: "Ferry origin and destination are the same field." };
  const departure = parseOpsDeparture(input.departureIso);
  if (!departure) return { ok: false, error: "Need a real STD (ISO time). Will not invent one." };
  const originRow = hydrateSceneryAirport(origin, [...input.scenery]);
  const destRow = hydrateSceneryAirport(dest, [...input.scenery]);
  if (!originRow || !destRow) {
    return { ok: false, error: `No Destinations fields for ${origin}–${dest}.` };
  }

  if (input.preferReinstate !== false && !input.forceCreate) {
    const cancelled = findCancelledFerryMatch(input.missions, {
      originIcao: origin,
      destIcao: dest,
      departureIso: departure.toISOString(),
      aircraftId: input.aircraftId,
      registration: input.registration,
    });
    if (cancelled) {
      const ete = cancelled.eteMin || input.eteMinHint || 60;
      const curfewWarning = opsFerryCurfewWarning(origin, dest, new Date(cancelled.departureIso), ete, input.hoursFor);
      return {
        ok: true,
        mode: "reinstate",
        mission: cancelled,
        curfewWarning,
        summary: scheduleFerryConfirmCopy({
          mode: "reinstate",
          originIcao: origin,
          destIcao: dest,
          departureIso: cancelled.departureIso,
          flightNumber: cancelled.flightNumber || cancelled.callsign,
          registration: cancelled.registration,
          curfewWarning,
        }),
      };
    }
  }

  const tail = pickOpsFerryTail(input.fleet, input.missions, input, origin);
  if (!tail.ok) return tail;
  const crew = pickOpsFerryCrew(input.crew ?? [], input.missions, tail.spec, input.crewId);
  const eteMin = input.eteMinHint && input.eteMinHint > 0 ? input.eteMinHint : 60;
  const curfewWarning = opsFerryCurfewWarning(origin, dest, departure, eteMin, input.hoursFor);
  return {
    ok: true,
    mode: "create",
    originIcao: origin,
    destIcao: dest,
    departureIso: departure.toISOString(),
    spec: tail.spec,
    crewId: crew.crewId,
    crewName: crew.crewName,
    eteMin,
    curfewWarning,
    summary: scheduleFerryConfirmCopy({
      mode: "create",
      originIcao: origin,
      destIcao: dest,
      departureIso: departure.toISOString(),
      registration: tail.spec.registration,
      curfewWarning,
    }),
  };
}

export function applyFerryToRevenue(
  mission: Mission,
  input: { paxCount: number; note?: string; revenueFactor?: number; kind?: "home" | "backhaul" | "fill" },
): { ok: true; mission: Mission } | { ok: false; error: string } {
  if (!isOpenOpsFerry(mission) && !(mission.flightType === "ferry" && mission.status === "pending")) {
    if (mission.flightType !== "ferry") {
      return { ok: false, error: `${spokenTripLabel(mission)} is already ${spokenFlightKind(mission)}.` };
    }
    return { ok: false, error: `${spokenTripLabel(mission)} is ${mission.status} — only an open ferry can take last-minute pax.` };
  }
  const pax = Math.round(input.paxCount);
  if (!Number.isFinite(pax) || pax < 1) {
    return { ok: false, error: "Need a pax count of at least 1 to convert a ferry to revenue." };
  }
  const note = String(input.note ?? "").trim();
  const kind = input.kind ?? "fill";
  const factor =
    typeof input.revenueFactor === "number" && input.revenueFactor > 0 && input.revenueFactor <= 1
      ? Math.round(input.revenueFactor * 1000) / 1000
      : FERRY_REVENUE_QUOTE_FACTOR;
  const purpose =
    kind === "backhaul"
      ? note
        ? `Backhaul fill — ${note}`
        : `Backhaul fill, ${pax} pax (empty return sold).`
      : kind === "home"
        ? note
          ? `Home-base fill — ${note}`
          : `Home-base fill, ${pax} pax (positioning F sold).`
        : note
          ? `Last-minute revenue charter — ${note}`
          : `Last-minute revenue charter, ${pax} pax (ferry fill).`;
  const discount =
    factor < 1
      ? `REVENUE  ${kind} ferry fill ×${factor} card (${Math.round((1 - factor) * 100)}% off)`
      : `REVENUE  ${kind} ferry fill at full card (discount unset)`;
  const next: Mission = {
    ...mission,
    flightType: FERRY_REVENUE_FLIGHT_TYPE,
    paxCount: pax,
    revenueFactor: factor,
    purpose,
    scenario: `Former empty ferry ${mission.originIcao}–${mission.destIcao} sold as a ${kind} charter. Same flight number.`,
    weatherNote: note || mission.weatherNote,
    mxPositioning: mission.isMaintenance ? true : mission.mxPositioning,
    dispatchWarning: [mission.dispatchWarning, discount].filter(Boolean).join(" · "),
  };
  return { ok: true, mission: next };
}

export function scheduleFerryConfirmCopy(input: {
  mode: OpsFerryMode;
  originIcao: string;
  destIcao: string;
  departureIso: string;
  flightNumber?: string;
  registration?: string;
  curfewWarning?: string;
}): string {
  const route = `${parseOpsIcao(input.originIcao)}–${parseOpsIcao(input.destIcao)}`;
  const when = (() => {
    const d = parseOpsDeparture(input.departureIso);
    return d ? formatUtcDateTime(d) : input.departureIso;
  })();
  const fn = input.flightNumber?.trim();
  const tail = input.registration?.trim();
  const id = [fn, tail].filter(Boolean).join(", ");
  const head =
    input.mode === "reinstate"
      ? `Put ferry ${id || route} back on the board (reinstate ${route} ${when})`
      : `Schedule ops ferry ${id ? `${id}, ` : ""}${route} ${when} — no Inbox RFQ`;
  return input.curfewWarning ? `${head}. ${input.curfewWarning}` : `${head}?`;
}

export function ferryToRevenueConfirmCopy(input: {
  label: string;
  paxCount: number;
  factor?: number;
}): string {
  const pct = Math.round((1 - (input.factor ?? FERRY_REVENUE_QUOTE_FACTOR)) * 100);
  return `Convert ferry ${input.label} to a light revenue charter, ${input.paxCount} pax, ${pct}% off the card (CAD)? Same flight number.`;
}
